/**
 * What this person has already found, in this browser.
 *
 * Two values are kept: whether space effects are switched on, and which
 * effect identifiers have run at least once. That is the whole record.
 *
 * What is deliberately **not** kept: the person's name, their email address,
 * their user identifier, the organization, the workspace, or anything from a
 * record. The storage key is an opaque digest of the user identifier, so one
 * browser can hold two people's preferences without either key saying who
 * they belong to.
 *
 * Discoveries are the same in every workspace of one person, because they
 * hold no workspace data. They are not shared between people: signing in as
 * somebody else reads that person's own key and nothing else.
 *
 * Storage can be refused — a private window, blocked site data, a browser
 * that throws on access. Every read and write is guarded, and the feature
 * falls back to memory for the session. Nothing here is important enough to
 * warrant a second attempt.
 */

import type { EffectId } from "./run.ts";
import { EFFECTS } from "./registry.ts";

const VERSION = 1;
const PREFIX = "arcgrc.space.";

/** Everything stored, for one person, in one browser. */
export interface DiscoveryState {
  readonly version: number;
  /** The person's own Space effects switch. */
  readonly enabled: boolean;
  readonly found: readonly EffectId[];
}

export const DEFAULT_STATE: DiscoveryState = {
  version: VERSION,
  enabled: true,
  found: [],
};

const KNOWN_IDS = new Set<string>(EFFECTS.map((entry) => entry.id));

/**
 * An opaque, stable key for one person.
 *
 * FNV-1a over the identifier. It is not a security measure and does not need
 * to be: its job is to keep a readable identifier out of the browser's
 * storage inspector while staying the same between visits.
 */
export function userKey(userId: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < userId.length; index += 1) {
    hash ^= userId.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${PREFIX}${hash.toString(16).padStart(8, "0")}`;
}

/** Read one person's state. Anything unexpected becomes the default. */
export function readState(key: string): DiscoveryState {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return DEFAULT_STATE;
  }
  if (raw === null) return DEFAULT_STATE;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return DEFAULT_STATE;
    const record = parsed as Record<string, unknown>;
    if (record["version"] !== VERSION) return DEFAULT_STATE;

    const enabled = typeof record["enabled"] === "boolean" ? record["enabled"] : true;
    const rawFound = Array.isArray(record["found"]) ? record["found"] : [];
    // An identifier this build does not know is dropped, so a downgrade or a
    // hand-edited value cannot put an unknown string into the registry.
    const found = rawFound.filter(
      (value): value is EffectId => typeof value === "string" && KNOWN_IDS.has(value),
    );
    return { version: VERSION, enabled, found };
  } catch {
    return DEFAULT_STATE;
  }
}

/** Write one person's state. A refusal is not an error worth showing. */
export function writeState(key: string, state: DiscoveryState): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage is unavailable or full. The session keeps its memory copy.
  }
}

/** How many effects must be found before the rover tows a trailer. */
export const TRAILER_AT = 5;

/**
 * The souvenirs the trailer carries.
 *
 * Derived from what was found, in registry order, so the trailer's contents
 * are the same every time rather than a new random pick. At most three.
 */
export function souvenirsFor(found: readonly EffectId[]): readonly string[] {
  const prop: Partial<Record<EffectId, string>> = {
    "space-duck": "duck",
    "lunar-footprints": "flag",
    "asteroid": "moon rock",
    "constellation": "star",
    "alien-inspection": "clipboard",
    "wormhole": "crate",
    "rocket-launch": "rocket",
    "eclipse": "moon",
  };
  const ordered = EFFECTS.map((entry) => entry.id).filter((id) => found.includes(id));
  const picked = ordered.map((id) => prop[id]).filter((name): name is string => name !== undefined);
  // A neutral star stands in when nothing specific was found.
  while (picked.length < Math.min(3, Math.max(1, found.length))) {
    picked.push("star");
  }
  return picked.slice(0, 3);
}
